import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Save, X, Copy, LayoutGrid } from 'lucide-react';
import { maquetaService } from '../../services/api';
import PlantillaCanvas from './PlantillaCanvas';

/**
 * PlantillasEditorPage — administración de plantillas de página, standalone
 * (sin depender de una edición real).
 *
 * Layout:
 *   ┌──────────────────┬───────────────────────────────────────┐
 *   │ Lista plantillas │  Editor de la plantilla activa        │
 *   │ + Nueva          │  (metadatos + canvas + tipo de slot)  │
 *   └──────────────────┴───────────────────────────────────────┘
 *
 * A diferencia de MaquetaTab (que trabaja con una edición y persiste cada
 * cambio al momento), aquí se trabaja en memoria y se guarda todo el
 * paquete (nombre + dimensiones + bloques) al hacer clic en "Guardar".
 */
export default function PlantillasEditorPage() {
  const [plantillas, setPlantillas] = useState([]);
  const [cargando, setCargando] = useState(true);
  // Plantilla activa en edición: null = ninguna | {id?, ...campos, bloques}
  const [activa, setActiva] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const recargar = useCallback(async () => {
    setCargando(true);
    try {
      const list = await maquetaService.listarPlantillas();
      setPlantillas(list);
    } catch (err) {
      console.error(err);
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { recargar(); }, [recargar]);

  const nuevaPlantilla = () => {
    setActiva({
      id: null,
      nombre: '',
      descripcion: '',
      columnas: 6,
      filas: 12,
      activa: true,
      bloques: [], // {_key, columnaInicio, filaInicio, columnasSpan, filasSpan, tipoSlot, etiqueta}
    });
  };

  const editarPlantilla = async (id) => {
    // Cargamos completa por si la lista viene sin bloques (defensa)
    try {
      const p = await maquetaService.listarPlantillas();
      const encontrada = p.find(x => x.id === id) || plantillas.find(x => x.id === id);
      if (!encontrada) return;
      // Añadir _key único a cada bloque para el state local del canvas
      setActiva({
        ...encontrada,
        bloques: (encontrada.bloques || []).map((b, i) => ({ ...b, _key: `db-${b.id ?? i}` })),
      });
    } catch (err) {
      alert('Error abriendo plantilla: ' + err.message);
    }
  };

  const duplicarPlantilla = (p) => {
    setActiva({
      id: null,
      nombre: `${p.nombre} (copia)`,
      descripcion: p.descripcion,
      columnas: p.columnas,
      filas: p.filas,
      activa: true,
      bloques: (p.bloques || []).map((b, i) => ({ ...b, _key: `dup-${i}` })),
    });
  };

  const eliminarPlantilla = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla? No se puede deshacer.')) return;
    try {
      await maquetaService.eliminarPlantilla(id);
      await recargar();
      if (activa?.id === id) setActiva(null);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const guardar = async () => {
    if (!activa) return;
    if (!activa.nombre.trim()) return alert('Ponle un nombre a la plantilla.');
    setGuardando(true);
    try {
      const dto = {
        nombre: activa.nombre.trim(),
        descripcion: activa.descripcion?.trim() || null,
        columnas: activa.columnas,
        filas: activa.filas,
        activa: activa.activa !== false,
        bloques: activa.bloques.map(b => ({
          columnaInicio: b.columnaInicio, filaInicio: b.filaInicio,
          columnasSpan: b.columnasSpan,   filasSpan:   b.filasSpan,
          tipoSlot:      b.tipoSlot || 'articulo',
          elementoData:  b.elementoData ?? null,
          etiqueta:      b.etiqueta ?? null,
        })),
      };
      if (activa.id) {
        await maquetaService.actualizarPlantilla(activa.id, dto);
      } else {
        const nueva = await maquetaService.crearPlantilla(dto);
        setActiva({ ...nueva, bloques: (nueva.bloques || []).map((b, i) => ({ ...b, _key: `db-${b.id ?? i}` })) });
      }
      await recargar();
      alert('Plantilla guardada.');
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ display:'flex', gap:12, height:'calc(100vh - 140px)', minHeight:600 }}>
      {/* ─── Lista de plantillas ────────────────────────────────── */}
      <aside style={{
        width:280, background:'#fafafa', border:'1px solid #e5e7eb',
        borderRadius:8, overflow:'hidden', display:'flex', flexDirection:'column',
      }}>
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', gap:6 }}>
          <LayoutGrid size={14} />
          <strong style={{ flex:1, fontSize:13, color:'#1B4332' }}>Plantillas ({plantillas.length})</strong>
          <button onClick={nuevaPlantilla} style={btnPrimary} title="Nueva plantilla en blanco">
            <Plus size={13} /> Nueva
          </button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:8 }}>
          {cargando && <div style={{ color:'#888', fontSize:12, padding:12 }}>Cargando…</div>}
          {!cargando && plantillas.length === 0 && (
            <div style={{ color:'#888', fontSize:12, padding:12, textAlign:'center' }}>
              No hay plantillas. Crea la primera con "+ Nueva".
            </div>
          )}
          {plantillas.map(p => (
            <div key={p.id} style={{
              background: activa?.id === p.id ? '#e8f5e9' : '#fff',
              border: `1px solid ${activa?.id === p.id ? '#1B4332' : '#ddd'}`,
              borderRadius:4, padding:10, marginBottom:6,
            }}>
              <div style={{ fontWeight:600, fontSize:13, color:'#1B4332', marginBottom:2 }}>
                {p.nombre}
                {!p.activa && <span style={{ marginLeft:6, fontSize:10, color:'#999' }}>(inactiva)</span>}
              </div>
              {p.descripcion && <div style={{ fontSize:11, color:'#666', marginBottom:4 }}>{p.descripcion}</div>}
              <div style={{ fontSize:10, color:'#888', marginBottom:6 }}>
                {p.columnas} col × {p.filas} filas · {p.bloques.length} bloques
              </div>
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={() => editarPlantilla(p.id)} style={btnGhost} title="Editar">
                  <Edit2 size={11} /> Editar
                </button>
                <button onClick={() => duplicarPlantilla(p)} style={btnGhost} title="Duplicar">
                  <Copy size={11} />
                </button>
                <button onClick={() => eliminarPlantilla(p.id)} style={{ ...btnGhost, color:'#dc2626', borderColor:'#fca5a5' }} title="Eliminar">
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* ─── Editor de la plantilla activa ──────────────────────── */}
      <main style={{ flex:1, display:'flex', flexDirection:'column', gap:8, minWidth:0 }}>
        {!activa ? (
          <div style={{
            flex:1, display:'flex', alignItems:'center', justifyContent:'center',
            background:'#fff', border:'1px solid #e5e7eb', borderRadius:8,
            color:'#888', fontSize:14, textAlign:'center', padding:40, lineHeight:1.5,
          }}>
            Selecciona una plantilla de la izquierda para editarla,<br/>
            o crea una nueva con el botón <strong>+ Nueva</strong>.
          </div>
        ) : (
          <>
            {/* Toolbar con metadatos */}
            <div style={{
              display:'flex', gap:8, alignItems:'center', padding:'10px 12px',
              background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, flexWrap:'wrap',
            }}>
              <input
                type="text" placeholder="Nombre de la plantilla *"
                value={activa.nombre}
                onChange={(e) => setActiva({ ...activa, nombre: e.target.value })}
                style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:4, fontSize:13, width:220 }}
              />
              <input
                type="text" placeholder="Descripción (opcional)"
                value={activa.descripcion || ''}
                onChange={(e) => setActiva({ ...activa, descripcion: e.target.value })}
                style={{ padding:'6px 10px', border:'1px solid #ddd', borderRadius:4, fontSize:13, flex:1, minWidth:180 }}
              />
              <label style={{ fontSize:12, display:'inline-flex', alignItems:'center', gap:4 }}>
                Filas:
                <input
                  type="number" min={4} max={40}
                  value={activa.filas}
                  onChange={(e) => setActiva({ ...activa, filas: Math.max(4, Math.min(40, parseInt(e.target.value, 10) || 12)) })}
                  style={{ width:56, padding:'4px 6px', border:'1px solid #ddd', borderRadius:4 }}
                />
              </label>
              <label style={{ fontSize:12, display:'inline-flex', alignItems:'center', gap:4 }}>
                <input type="checkbox" checked={activa.activa !== false}
                  onChange={(e) => setActiva({ ...activa, activa: e.target.checked })}
                /> Activa
              </label>
              <button onClick={() => setActiva(null)} style={btnGhost}>
                <X size={13} /> Cerrar
              </button>
              <button onClick={guardar} disabled={guardando} style={btnPrimary}>
                <Save size={13} /> {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>

            {/* Info tipos de slot */}
            <div style={{
              padding:'8px 12px', background:'#fff', border:'1px solid #e5e7eb',
              borderRadius:8, fontSize:12, color:'#555',
            }}>
              Añade bloques arrastrando desde los botones. Cada bloque tiene un <strong>tipo de slot</strong> que
              indica qué va a llenarlo cuando se aplique la plantilla a una página real: <span style={{ color:'#1B4332', fontWeight:600 }}>Artículo</span>,
              <span style={{ color:'#1e40af', fontWeight:600 }}> Pauta</span> o
              <span style={{ color:'#78350f', fontWeight:600 }}> Elemento fijo</span> (cabezotes, folios, etc.).
            </div>

            {/* Canvas */}
            <div style={{ flex:1, overflow:'auto', background:'#e5e7eb', padding:20, borderRadius:8 }}>
              <PlantillaCanvas
                plantilla={activa}
                onCambio={(bloques) => setActiva({ ...activa, bloques })}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const btnGhost = {
  background:'#fff', border:'1px solid #ddd', borderRadius:4,
  padding:'4px 8px', cursor:'pointer', fontSize:11, color:'#555',
  display:'inline-flex', alignItems:'center', gap:4,
};
const btnPrimary = {
  background:'#1B4332', color:'#fff', border:'none', borderRadius:4,
  padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:600,
  display:'inline-flex', alignItems:'center', gap:6,
};
